import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Play, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import type { Settings, SettingsInput } from '@fenix/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { autopilotApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { fmtRelative } from '@/lib/format';

interface Props {
  form: SettingsInput;
  set: <K extends keyof SettingsInput>(k: K, v: SettingsInput[K]) => void;
  settings: Settings;
  isAdmin: boolean;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-3 py-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}

/**
 * Piloto automático: a cada ciclo, matricula os próximos leads elegíveis na campanha do
 * sistema. Os campos salvam pelo "Salvar" da página; ligar/desligar vale na hora.
 */
export function AutopilotCard({ form, set, settings: s, isAdmin }: Props) {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['autopilot'],
    queryFn: autopilotApi.status,
    refetchInterval: 10_000,
  });
  const run = useMutation({
    mutationFn: autopilotApi.run,
    onSuccess: (r) => {
      toast.success(
        r.enrolled
          ? `${r.enrolled} lead(s) matriculado(s) — ${r.eligible} elegível(is) no total`
          : `Nenhum lead elegível agora (${r.eligible} no total)`,
      );
      void qc.invalidateQueries({ queryKey: ['autopilot'] });
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Falha ao executar'),
  });

  const st = status.data;
  const enabled = form.autopilotEnabled ?? s.autopilotEnabled;
  const channel = form.autopilotChannel ?? s.autopilotChannel;
  const dirty = form.autopilotEnabled !== undefined && form.autopilotEnabled !== s.autopilotEnabled;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Piloto automático</CardTitle>
        <CardDescription>
          Liga sozinho para os carrinhos novos: a cada ciclo, matricula os próximos leads elegíveis
          na campanha &quot;Piloto automático&quot;. Discar, retentar e respeitar a janela de
          horário é a campanha normal.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <Label>Ligado</Label>
            <p className="text-xs text-muted-foreground">
              Desligar pausa a campanha do piloto na hora — nada mais disca. Ligar retoma.
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(v) => set('autopilotEnabled', v)}
            disabled={!isAdmin}
          />
        </div>
        {dirty && <p className="text-xs text-warning">Salve para aplicar.</p>}

        <div className="space-y-1.5">
          <Label>Canal</Label>
          <Select
            value={channel}
            onValueChange={(v) => set('autopilotChannel', v as 'phone' | 'web')}
            disabled={!isAdmin}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="phone">Telefone — disca para o lead (produção)</SelectItem>
              <SelectItem value="web">
                Navegador — deixa a ligação pronta para atender (teste)
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            No canal navegador ninguém é discado: o painel avisa que há uma ligação pronta e você
            atende como o cliente. Todo o resto — matrícula, tentativas, resultado — é igual ao
            telefone.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Ligações por ciclo</Label>
            <Input
              type="number"
              min={1}
              max={500}
              value={form.autopilotLeadsPerRun ?? s.autopilotLeadsPerRun}
              onChange={(e) => set('autopilotLeadsPerRun', Number(e.target.value) || 1)}
              disabled={!isAdmin}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Ciclo (minutos)</Label>
            <Input
              type="number"
              min={1}
              max={1440}
              value={form.autopilotIntervalMin ?? s.autopilotIntervalMin}
              onChange={(e) => set('autopilotIntervalMin', Number(e.target.value) || 1)}
              disabled={!isAdmin}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Esperar após o abandono (min)</Label>
            <Input
              type="number"
              min={0}
              max={10080}
              value={form.autopilotDelayMin ?? s.autopilotDelayMin}
              onChange={(e) => set('autopilotDelayMin', Number(e.target.value) || 0)}
              disabled={!isAdmin}
            />
            <p className="text-xs text-muted-foreground">
              Ligar 10 s depois assusta; 30 min converte.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Ignorar carrinhos com mais de (dias)</Label>
            <Input
              type="number"
              min={1}
              max={365}
              value={form.autopilotMaxAgeDays ?? s.autopilotMaxAgeDays}
              onChange={(e) => set('autopilotMaxAgeDays', Number(e.target.value) || 1)}
              disabled={!isAdmin}
            />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Países dos leads (ISO, separados por vírgula)</Label>
            <Input
              placeholder="US"
              value={form.autopilotCountries ?? s.autopilotCountries}
              onChange={(e) => set('autopilotCountries', e.target.value)}
              disabled={!isAdmin}
            />
            <p className="text-xs text-muted-foreground">
              Só leads com telefone válido e destes países entram. Número comprado do Retell alcança
              apenas os EUA — deixe &quot;US&quot; até haver telefonia para outros países.
            </p>
          </div>
        </div>

        <div className="divide-y rounded-lg border text-sm">
          <Row label="Worker">
            {st?.workerOnline ? (
              <Badge variant="secondary" className="gap-1">
                <CheckCircle2 className="size-3 text-success" /> no ar
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1">
                <XCircle className="size-3 text-destructive" /> parado
              </Badge>
            )}
          </Row>
          <Row label="Elegíveis agora">
            {st ? `${st.eligibleNow} · países: ${st.countries.join(', ')}` : '—'}
          </Row>
          <Row label="Último ciclo">
            {st?.lastRunAt
              ? `${fmtRelative(st.lastRunAt)} · ${st.lastEnrolled} matriculado(s) de ${st.lastEligible} elegível(is)`
              : 'nunca'}
          </Row>
          <Row label="Próximo ciclo">{st?.nextRunAt ? fmtRelative(st.nextRunAt) : '—'}</Row>
          <Row label="Campanha">
            {st?.campaign ? (
              <Link className="underline" to={`/campanhas/${st.campaign.id}`}>
                {st.campaign.completedLeads}/{st.campaign.totalLeads} ligados ·{' '}
                {st.campaign.recoveredLeads} recuperados
              </Link>
            ) : (
              'criada no primeiro ciclo'
            )}
          </Row>
        </div>

        {st?.channel === 'web' && st.pendingWeb.length > 0 && (
          <Alert>
            <AlertDescription>
              {st.pendingWeb.length} ligação(ões) pronta(s) para atender pelo navegador — o aviso no
              topo da tela leva até ela.
            </AlertDescription>
          </Alert>
        )}

        {isAdmin && (
          <Button variant="outline" onClick={() => run.mutate()} disabled={run.isPending}>
            {run.isPending ? <Loader2 className="animate-spin" /> : <Play />} Executar um ciclo
            agora
          </Button>
        )}
        <p className="text-xs text-muted-foreground">
          Elegível = lead novo, de produto ativo, fora de campanha aberta e da lista &quot;não
          ligar&quot;, com o carrinho dentro da janela de idade. Leads de exemplo nunca entram.
          Simultaneidade e chamadas/minuto continuam valendo, ao lado.
        </p>
      </CardContent>
    </Card>
  );
}
